import { useEffect, useRef, useState } from 'react';
import api from '../api';

/**
 * Custom hook for AURA WebSocket connection.
 * Manages real-time alerts and market updates.
 */
export default function useAuraWebSocket(onAlert, enabled = true) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [auraWelcomeMessage, setAuraWelcomeMessage] = useState(''); // Isolated welcome message state
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const onAlertRef = useRef(onAlert);
  const maxReconnectAttempts = 5;

  // Update callback ref when it changes (without causing reconnection)
  useEffect(() => {
    onAlertRef.current = onAlert;
  }, [onAlert]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Get WebSocket URL from API base URL
    const getWebSocketUrl = () => {
      // Use REACT_APP_API_URL from environment, or fallback to api.defaults.baseURL, or window.location.origin
      const apiBaseUrl = process.env.REACT_APP_API_URL || api.defaults.baseURL || window.location.origin;
      
      // Ensure we have a valid URL
      let baseUrl = apiBaseUrl;
      if (!baseUrl) {
        console.warn('⚠️ No API URL found, using window.location.origin');
        baseUrl = window.location.origin;
      }
      
      // Remove trailing slash if present
      baseUrl = baseUrl.replace(/\/$/, '');
      
      // Convert http:// to ws:// and https:// to wss://
      // Handle both localhost and Render URLs
      const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
      const wsBaseUrl = baseUrl.replace(/^https?/, wsProtocol);
      
      // Get auth token from localStorage or API defaults
      const token = localStorage.getItem('token') || api.defaults.headers.common['Authorization']?.replace('Bearer ', '');
      const userId = localStorage.getItem('userId'); // You may need to store this
      
      // Build WebSocket URL with auth
      const wsUrl = `${wsBaseUrl}/api/v1/robot/ws/robot-updates`;
      const params = new URLSearchParams();
      if (userId) params.append('user_id', userId);
      if (token) params.append('token', token);
      
      const finalUrl = params.toString() ? `${wsUrl}?${params.toString()}` : wsUrl;
      console.log('🔌 AURA WebSocket URL:', finalUrl.replace(/token=[^&]*/, 'token=***')); // Log without exposing token
      return finalUrl;
    };

    const connect = () => {
      // Don't connect if already connecting or connected
      if (wsRef.current && (wsRef.current.readyState === WebSocket.CONNECTING || wsRef.current.readyState === WebSocket.OPEN)) {
        console.log('⚠️ AURA WebSocket already connecting/connected, skipping...');
        return;
      }

      try {
        const wsUrl = getWebSocketUrl();
        console.log('🔌 Connecting to AURA WebSocket:', wsUrl.replace(/token=[^&]*/, 'token=***'));
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        // Set up handlers immediately to catch any early events
        ws.onopen = () => {
          console.log('✅ AURA WebSocket connected successfully');
          setIsConnected(true);
          setConnectionError(null);
          reconnectAttemptsRef.current = 0;
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Handle ping messages (keepalive) - respond with pong
            if (data.type === 'ping') {
              try {
                ws.send(JSON.stringify({ type: 'pong', timestamp: new Date().toISOString() }));
                console.log('🏓 AURA WebSocket: Responded to ping');
              } catch (error) {
                console.warn('Could not send pong response:', error);
              }
              return; // Don't process ping as a regular message
            }
            
            console.log('📨 AURA WebSocket message:', data);
            
            // Handle different message types
            if (data.type === 'connection' || data.type === 'welcome') {
              // CRITICAL: Set welcome message state ONCE - isolated from render cycle
              const welcomeText = data.voice_script || data.message || data.text || 'Connected to AURA';
              console.log('👋 AURA welcome message received via WebSocket:', welcomeText.substring(0, 50));
              setAuraWelcomeMessage(welcomeText);
              
              // Also pass to alert handler for UI display
              if (onAlertRef.current) {
                onAlertRef.current({
                  message: data.message || data.voice_script || data.text || 'Connected to AURA',
                  voice_script: data.voice_script || data.message || data.text,
                  text: data.text || data.message || data.voice_script,
                  type: 'connection',
                  priority: 'low',
                  data: data
                });
              }
            } else if (data.type === 'signal_change' || 
                       data.type === 'price_level' || 
                       data.type === 'setup_formation' ||
                       data.type === 'auto_trade_started' ||
                       data.type === 'auto_trade_stopped' ||
                       data.type === 'auto_trade_success' ||
                       data.type === 'auto_trade_error' ||
                       data.type === 'auto_trade_target_hit' ||
                       data.type === 'auto_trade_stop_loss_hit') {
              // Trigger alert callback for trading-related events
              if (onAlertRef.current) {
                onAlertRef.current({
                  message: data.message || data.voice_script || data.text,
                  voice_script: data.voice_script || data.message || data.text,
                  text: data.text || data.message || data.voice_script,
                  type: data.type,
                  priority: data.priority || 'high',
                  data: data
                });
              }
            } else {
              // Handle any other message types (including welcome messages with voice_script)
              if (data.message || data.voice_script || data.text) {
                if (onAlertRef.current) {
                  onAlertRef.current({
                    message: data.message || data.voice_script || data.text,
                    voice_script: data.voice_script || data.message || data.text,
                    text: data.text || data.message || data.voice_script,
                    type: data.type || 'message',
                    priority: data.priority || 'low',
                    data: data
                  });
                }
              }
            }
          } catch (error) {
            console.error('Error parsing AURA WebSocket message:', error);
          }
        };

        ws.onerror = (error) => {
          console.error('❌ AURA WebSocket error:', error);
          // Don't set error state immediately - wait for onclose to get the actual error code
          // This prevents false errors during normal connection attempts
        };

        ws.onclose = (event) => {
          console.log(`🔌 AURA WebSocket closed: code=${event.code}, reason="${event.reason || 'none'}", wasClean=${event.wasClean}`);
          setIsConnected(false);
          wsRef.current = null;

          // Only attempt to reconnect if it wasn't a normal closure and we haven't exceeded max attempts
          if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000); // Exponential backoff, max 30s
            reconnectAttemptsRef.current++;
            console.log(`🔄 Reconnecting to AURA WebSocket in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, delay);
          } else {
            if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
              setConnectionError('Failed to reconnect after multiple attempts');
              console.error('❌ AURA WebSocket: Max reconnection attempts reached');
            } else if (event.code === 1000) {
              console.log('✅ AURA WebSocket closed normally (code 1000)');
            } else {
              setConnectionError(`WebSocket closed unexpectedly (code: ${event.code})`);
            }
          }
        };
      } catch (error) {
        console.error('Error creating AURA WebSocket connection:', error);
        setConnectionError(error.message);
        setIsConnected(false);
        
        // Attempt to reconnect on error
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectAttemptsRef.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      }
    };

    // Initial connection
    connect();

    // Cleanup
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
      setIsConnected(false);
    };
  }, [enabled]); // Removed onAlert from dependencies - using ref instead

  return {
    isConnected,
    connectionError,
    auraWelcomeMessage, // Expose welcome message state
    reconnect: () => {
      reconnectAttemptsRef.current = 0;
      if (wsRef.current) {
        wsRef.current.close();
      }
      // Connection will be re-established by useEffect
    }
  };
}

